<template>
    <!-- An explanation of what it is on: a Vuetify tooltip at once on hover (desktop), on a tap where there is no
         hover (mobile), closed by a tap anywhere else or a scroll. `text`, or the `content` slot for more -->
    <v-tooltip
        v-model="open"
        :top="!bottom"
        :bottom="bottom"
        :max-width="maxWidth"
        :content-class="['info-tip__content', contentClass].filter(Boolean).join(' ')"
        :open-on-hover="!touch"
        :open-on-focus="!touch"
        :open-on-click="false"
        :disabled="disabled"
    >
        <template #activator="{ on, attrs }">
            <span
                ref="activator"
                class="info-tip"
                :class="{ 'info-tip--text': underline, 'info-tip--block': block }"
                :style="activatorStyle"
                tabindex="0"
                v-bind="attrs"
                v-on="on"
                @click="onTap"
            >
                <slot />
            </span>
        </template>
        <slot name="content">
            <span>{{ text }}</span>
        </slot>
    </v-tooltip>
</template>

<script>
    // no hover: a tap opens the tip (the browsers fake a hover on a tap, the tip then never closes or flickers)
    const touch = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: none)').matches
    // one tip open at a time on a phone
    let current = null

    export default {
        name: 'InfoTip',
        props: {
            text: { type: String, default: '' },
            bottom: { type: Boolean, default: false },
            maxWidth: { type: [Number, String], default: 300 },
            contentClass: { type: String, default: '' },
            // a dotted underline: there is something to read on the words
            underline: { type: Boolean, default: false },
            // the activator fills what it is in (a bar, a segment)
            block: { type: Boolean, default: false },
            // the style of the element it is on (a class or a style on <info-tip> goes to the hidden tooltip root)
            activatorStyle: { type: Object, default: null },
            // a tap does what it does under the tip too (sorts a column, opens a page)
            passTap: { type: Boolean, default: false },
            // only on hover: on a phone the tap is for what it is on
            hoverOnly: { type: Boolean, default: false }
        },
        data() {
            return {
                open: false,
                touch
            }
        },
        computed: {
            disabled() {
                return (!this.text && !this.$scopedSlots.content) || (this.touch && this.hoverOnly)
            }
        },
        watch: {
            open(value) {
                if (!this.touch) return
                if (value) {
                    if (current && current !== this) current.open = false
                    current = this
                    document.addEventListener('touchstart', this.onOutside, true)
                    document.addEventListener('click', this.onOutside, true)
                    window.addEventListener('scroll', this.close, true)
                } else {
                    if (current === this) current = null
                    this.unlisten()
                }
            }
        },
        beforeDestroy() {
            if (current === this) current = null
            this.unlisten()
        },
        methods: {
            onTap(event) {
                if (!this.touch || this.disabled) return
                if (!this.passTap) event.stopPropagation()
                this.open = !this.open
            },
            close() {
                this.open = false
            },
            onOutside(event) {
                const activator = this.$refs.activator
                if (activator && activator.contains(event.target)) return
                this.close()
            },
            unlisten() {
                document.removeEventListener('touchstart', this.onOutside, true)
                document.removeEventListener('click', this.onOutside, true)
                window.removeEventListener('scroll', this.close, true)
            }
        }
    }
</script>

<style scoped>
.info-tip {
    cursor: help;
    outline: none;
}

.info-tip--text {
    text-decoration: underline dotted;
    text-decoration-color: currentColor;
    text-underline-offset: 3px;
}

.info-tip--block {
    display: block;
    width: 100%;
    height: 100%;
}
</style>

<style>
.info-tip__content {
    font-size: 0.8rem;
    line-height: 1.35;
}
</style>
